'use client'

import { useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { roundOf32, roundOf32Summary } from '@/content/world-cup-2026/data'
import type { KnockoutMatch } from '@/content/world-cup-2026/types'

import { WcMatch } from './wc-match'

interface DateGroup {
    date: string
    matches: KnockoutMatch[]
}

/** Group consecutive matches by their (already date-ordered) date. */
const groupByDate = (matches: KnockoutMatch[]): DateGroup[] => {
    const groups: DateGroup[] = []
    for (const match of matches) {
        const last = groups.at(-1)
        if (last?.date === match.date) {
            last.matches.push(match)
        } else {
            groups.push({ date: match.date, matches: [match] })
        }
    }
    return groups
}

/**
 * Round-of-32 knockout fixtures: every R32 game hosted in a US city, grouped by
 * date, with the host-city jump link for each — matchup, date, city; no results.
 * Collapsible via a native <details> — same affordance as WcMasterSchedule — but
 * defaults open so the fixtures show without a click. Renders server-side so the table
 * stays crawlable when collapsed.
 */
export const WcRoundOf32Schedule = () => {
    const [open, setOpen] = useState(true)
    const dateGroups = groupByDate(roundOf32)

    return (
        <section id="round-of-32" className="mb-[52px] scroll-mt-[124px]">
            <details
                className="overflow-hidden rounded-lg shadow-[var(--wc-shadow-1)]"
                open={open}
                onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
                <summary className="flex cursor-pointer list-none items-center justify-between bg-[var(--wc-blu-900)] px-5 py-4 text-[15px] font-bold text-white [&::-webkit-details-marker]:hidden">
                    <span>{roundOf32Summary}</span>
                    <span className="inline-flex items-center gap-[7px] text-[13px] font-semibold text-[var(--wc-blu-300)]">
                        <ChevronDown
                            className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
                            aria-hidden="true"
                        />
                        {open ? 'Hide matches' : 'Show all matches'}
                    </span>
                </summary>

                <div className="overflow-hidden border border-t-0 border-[var(--wc-border)]">
                    <table className="w-full border-collapse text-sm">
                        <caption className="sr-only">
                            All US Round-of-32 matches, grouped by date
                        </caption>
                        <tbody>
                            {dateGroups.map((g) => (
                                <DateRows key={g.date} group={g} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </section>
    )
}

const DateRows = ({ group }: { group: DateGroup }) => (
    <>
        <tr>
            <td
                colSpan={2}
                className="border-b border-[var(--wc-border)] bg-[var(--wc-smoke-50)] px-4 py-[9px] text-xs font-bold tracking-[0.04em] text-[var(--wc-fg-2)] uppercase"
            >
                {group.date}
            </td>
        </tr>
        {group.matches.map((m, i) => (
            <tr
                key={`${m.home}-${m.away}-${i}`}
                data-testid="r32-match-row"
                className="hover:bg-[var(--wc-blu-25)]"
            >
                <td className="border-b border-[var(--wc-border-light)] px-4 py-[11px] align-middle font-medium">
                    <WcMatch home={m.home} away={m.away} />
                </td>
                <td className="border-b border-[var(--wc-border-light)] px-4 py-[11px] text-right align-middle text-xs font-semibold">
                    <a href={`#${m.stadiumAnchor}`} className="text-[var(--wc-blu-700)]">
                        {m.stadiumLabel}
                    </a>
                </td>
            </tr>
        ))}
    </>
)
